import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import OfflineBanner from './OfflineBanner';
import { 
  LayoutDashboard, 
  Building2, 
  Truck, 
  Receipt,
  LogOut,
  ChevronRight,
  Database,
  Users,
  Shield,
  Menu,
  X,
  BookUser,
  Activity,
  Archive,
  MessageSquare,
  Banknote
} from 'lucide-react';

const Layout = ({ children }) => {
  const { user, logout, isAdmin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Fermer le menu mobile lors d'un changement de page
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const navItems = [
    { to: '/', icon: LayoutDashboard, label: 'Tableau de bord' },
    { to: '/clients', icon: Building2, label: 'Enseignes' },
    { to: '/contacts', icon: BookUser, label: 'Contacts' },
    { to: '/base-maag', icon: Database, label: 'Base MAAG' },
    { to: '/fournisseurs', icon: Truck, label: 'Fournisseurs' },
    { to: '/frais', icon: Receipt, label: 'Frais' },
    { to: '/archives', icon: Archive, label: 'Archives' },
    { to: '/modifications', icon: MessageSquare, label: 'Modifications app' },
  ];

  // Admin nav items
  const adminNavItems = [
    { to: '/admin/users', icon: Users, label: 'Utilisateurs' },
    { to: '/admin/suivi-bancaire', icon: Banknote, label: 'Suivi bancaire' },
    { to: '/admin/diagnostics', icon: Activity, label: 'Diagnostics' },
  ];

  return (
    <div className="min-h-screen bg-slate-50 flex" data-testid="main-layout">
      {/* Mobile Header — utilise safe-area-inset-top pour ne PAS chevaucher
       * la barre système iOS/Android (heure, batterie, 4G…). */}
      <div
        className="lg:hidden fixed top-0 left-0 right-0 bg-slate-900 z-50 flex items-center justify-between px-4"
        style={{
          paddingTop: 'env(safe-area-inset-top, 0px)',
          height: 'calc(4rem + env(safe-area-inset-top, 0px))',
        }}
      >
        <div className="flex items-center">
          <img 
            src="https://customer-assets.emergentagent.com/job_parlez-amis/artifacts/ief9lfq6_image.png" 
            alt="MAAG" 
            className="w-8 h-8 mr-3 object-contain"
          />
          <span className="text-xl font-bold text-white">MAAG</span>
        </div>
        <button
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className="p-2 text-white hover:bg-slate-800 rounded-md"
          data-testid="mobile-menu-btn"
        >
          {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Mobile Overlay */}
      {mobileMenuOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        w-64 lg:w-72 bg-slate-900 text-white flex flex-col fixed h-full z-50
        transition-transform duration-300 ease-in-out
        lg:translate-x-0
        ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}
        lg:top-0 top-16
      `}>
        <div className="p-6 border-b border-slate-700 hidden lg:block">
          <div className="flex items-center">
            <img 
              src="https://customer-assets.emergentagent.com/job_parlez-amis/artifacts/ief9lfq6_image.png" 
              alt="MAAG" 
              className="w-8 h-8 mr-3 object-contain"
            />
            <span className="text-xl font-bold">MAAG</span>
          </div>
        </div>

        <nav className="flex-1 p-4 overflow-y-auto">
          <ul className="space-y-1">
            {navItems.map((item) => (
              <li key={item.to}>
                <NavLink
                  to={item.to}
                  end={item.to === '/'}
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
                  className={({ isActive }) =>
                    `flex items-center px-4 py-3 rounded-md text-sm font-medium transition-colors ${
                      isActive
                        ? 'bg-slate-800 text-white'
                        : 'text-slate-300 hover:bg-slate-800 hover:text-white'
                    }`
                  }
                >
                  <item.icon className="w-5 h-5 mr-3" />
                  {item.label}
                  <ChevronRight className="w-4 h-4 ml-auto opacity-50" />
                </NavLink>
              </li>
            ))}
          </ul>

          {/* Admin section */}
          {isAdmin && (
            <>
              <div className="mt-6 mb-2 px-4 py-2 text-xs font-semibold text-slate-500 uppercase tracking-wider flex items-center">
                <Shield className="w-3 h-3 mr-2" />
                Administration
              </div>
              <ul className="space-y-1">
                {adminNavItems.map((item) => (
                  <li key={item.to}>
                    <NavLink
                      to={item.to}
                      data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
                      className={({ isActive }) =>
                        `flex items-center px-4 py-3 rounded-md text-sm font-medium transition-colors ${
                          isActive
                            ? 'bg-red-900/50 text-red-200'
                            : 'text-slate-300 hover:bg-slate-800 hover:text-white'
                        }`
                      }
                    >
                      <item.icon className="w-5 h-5 mr-3" />
                      {item.label}
                      <ChevronRight className="w-4 h-4 ml-auto opacity-50" />
                    </NavLink>
                  </li>
                ))}
              </ul>
            </>
          )}
        </nav>

        <div className="p-4 border-t border-slate-700">
          <div className="px-4 py-2 text-sm text-slate-400 mb-2">
            <div className="flex items-center">
              {user?.nom || user?.email}
              {isAdmin && (
                <span className="ml-2 px-1.5 py-0.5 text-xs bg-red-600 text-white rounded">
                  Admin
                </span>
              )}
            </div>
            {user?.role && user.role !== 'admin' && (
              <span className="text-xs text-slate-500 capitalize">{user.role}</span>
            )}
          </div>
          <button
            data-testid="logout-btn"
            onClick={handleLogout}
            className="w-full flex items-center px-4 py-3 rounded-md text-sm font-medium text-slate-300 hover:bg-slate-800 hover:text-white transition-colors"
          >
            <LogOut className="w-5 h-5 mr-3" />
            Déconnexion
          </button>
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 lg:ml-72 ml-0 min-w-0 overflow-x-hidden">
        <OfflineBanner />
        <div className="p-4 lg:p-8 pt-[calc(5rem+env(safe-area-inset-top,0px))] lg:pt-8 min-w-0">
          {children}
        </div>
      </main>
    </div>
  );
};

export default Layout;
